/**
 * 框架通道配置页的纯派生：商户账户组分组、五步接入进度、连接状态口径、未配置 App 候选与原生回跳 host 拆装。
 *
 * 只从 `ProviderConfigResponse[]`（A8 §10.1）、能力声明（§10.0）与 App 列表派生，不编造状态：
 *   - 商户账户组 = 同一 `(merchant_account_id, mode)`；组内 endpoint 列表取任一成员的 `webhook_endpoints[]`
 *     （契约保证同组成员返回同一列表）。
 *   - 进度 ①–⑤：有配置 → 组内有 endpoint → 组内有启用中且 `verifying_material_count > 0` 的 endpoint →
 *     `connection_status = connected` → `is_active`。停在第一个未满足的步骤。
 *   - 「需重新测试」= `last_test_result.config_version ≠ config_version`（§10.1 读取判据）。
 */

import type {
  PaymentProviderChannel,
  PaymentProviderConfigResponse,
  PaymentProviderConnectionStatus,
  PaymentProviderMode,
  PaymentProviderWebhookEndpointSummary,
} from '@novel-admin/shared';
import type { AppRow } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { providerChannelCopy } from './provider-channel-copy';

export type ProviderPlatform = 'IOS' | 'ANDROID' | 'WEB';

/** 原生回跳基础 URL 的固定 path（A8 §10.1：须恰为 `https://<host>/payment/checkout/success`）。 */
export const NATIVE_RETURN_PATH = '/payment/checkout/success';

/** 与服务端 / 字典 65 §1 CHECK 同一形态：小写 host，无端口、userinfo、query、fragment。 */
const NATIVE_RETURN_URL_PATTERN = /^https:\/\/([a-z0-9.-]+)\/payment\/checkout\/success$/;
const HOST_PATTERN = /^[a-z0-9.-]+$/;

/** AppRow.platform（展示态 iOS / Android / WEB）→ 能力声明 platforms 的取值。 */
export function providerPlatformOf(
  app: Pick<AppRow, 'platform'> | undefined,
): ProviderPlatform | null {
  switch (app?.platform) {
    case 'iOS':
      return 'IOS';
    case 'Android':
      return 'ANDROID';
    case 'WEB':
      return 'WEB';
    default:
      return null;
  }
}

export function isNativePlatform(platform: ProviderPlatform | null): boolean {
  return platform === 'IOS' || platform === 'ANDROID';
}

export function nativeReturnHostOf(url: string | null | undefined): string {
  if (!url) return '';
  return NATIVE_RETURN_URL_PATTERN.exec(url)?.[1] ?? '';
}

export function nativeReturnUrlFor(host: string): string {
  return `https://${host}${NATIVE_RETURN_PATH}`;
}

/** 规范化运营录入的 host：去首尾空白并转小写（服务端只接受小写 host）。 */
export function normalizeReturnHost(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidReturnHost(host: string): boolean {
  return host.length > 0 && HOST_PATTERN.test(host);
}

/** 密钥材料本地规范化（去 PEM 头尾与全部空白），只用于前端判空，提交仍发原文由服务端规范化。 */
export function normalizeKeyMaterial(material: string): string {
  return material.replace(/-----(BEGIN|END)[A-Z0-9 ]+-----/g, '').replace(/\s+/g, '');
}

export type MerchantGroup = {
  key: string;
  merchantAccountId: string;
  mode: PaymentProviderMode;
  configs: PaymentProviderConfigResponse[];
  endpoints: PaymentProviderWebhookEndpointSummary[];
};

export function merchantGroupKey(merchantAccountId: string, mode: PaymentProviderMode): string {
  return `${mode}:${merchantAccountId}`;
}

/** 按 `(merchant_account_id, mode)` 分组；组按商户号、mode 排序，组内 App 保持列表原序。 */
export function groupConfigsByMerchant(configs: PaymentProviderConfigResponse[]): MerchantGroup[] {
  const groups = new Map<string, MerchantGroup>();
  for (const config of configs) {
    const key = merchantGroupKey(config.merchant_account_id, config.mode);
    const existing = groups.get(key);
    if (existing) {
      existing.configs.push(config);
    } else {
      groups.set(key, {
        key,
        merchantAccountId: config.merchant_account_id,
        mode: config.mode,
        configs: [config],
        endpoints: config.webhook_endpoints,
      });
    }
  }
  return [...groups.values()].sort((a, b) =>
    a.merchantAccountId === b.merchantAccountId
      ? a.mode.localeCompare(b.mode)
      : a.merchantAccountId.localeCompare(b.merchantAccountId),
  );
}

/** 组内是否有「启用中且有当前有效验签材料」的 endpoint（进度第 ③ 步判据）。 */
export function hasActiveVerifiedEndpoint(
  endpoints: PaymentProviderWebhookEndpointSummary[],
): boolean {
  return endpoints.some((e) => e.is_active && e.verifying_material_count > 0);
}

/** 组内是否已有验签材料（任一未退役 endpoint 的材料数 > 0）：有则新写入即整组轮转。 */
export function groupHasMaterial(endpoints: PaymentProviderWebhookEndpointSummary[]): boolean {
  return endpoints.some((e) => e.verifying_material_count > 0);
}

/** 五步接入进度的步骤名（与通道无关）。 */
export const SETUP_STEP_TITLES = [
  '保存配置',
  '登记回调',
  '写入验签材料',
  '测试连接',
  '启用',
] as const;

/** 五步接入进度（步骤名 + 按通道的说明）：第 ②③ 步的供应商后台与验签材料称呼随通道。 */
export function setupStepsFor(
  channel: PaymentProviderChannel,
): readonly { title: string; desc: string }[] {
  const copy = providerChannelCopy(channel);
  const descs = [
    '商户号、mode、凭据，原生 App 另填回跳 host',
    `按商户账户组登记，复制地址填入${copy.consoleName}`,
    copy.verificationCredentialField === null
      ? `${copy.materialName}，在${copy.consoleName}的 webhook 订阅取得，不属于凭据`
      : `${copy.materialName}，与凭据里的是同一把`,
    '只读探针，不建单不核价',
    '要求已连接；连接正常不代表可以收款',
  ];
  return SETUP_STEP_TITLES.map((title, index) => ({ title, desc: descs[index] ?? '' }));
}

/** 当前停在第几步（1–5）；全部满足返回 null。 */
export function setupCurrentStep(
  config: Pick<PaymentProviderConfigResponse, 'connection_status' | 'is_active'>,
  endpoints: PaymentProviderWebhookEndpointSummary[],
): number | null {
  const met = [
    true,
    endpoints.length > 0,
    hasActiveVerifiedEndpoint(endpoints),
    config.connection_status === 'connected',
    config.is_active,
  ];
  const firstUnmet = met.indexOf(false);
  return firstUnmet < 0 ? null : firstUnmet + 1;
}

/**
 * 配置侧是否满足 C 端下发条件：已启用 + 已连接，原生 App 另需回跳 URL 非空。
 * 商户合约、支付方式开通与商品价格不在本页判定。
 */
export function isConfigSideReady(
  config: Pick<
    PaymentProviderConfigResponse,
    'is_active' | 'connection_status' | 'native_return_base_url'
  >,
  platform: ProviderPlatform | null,
): boolean {
  if (!config.is_active || config.connection_status !== 'connected') return false;
  return platform === 'WEB' || config.native_return_base_url !== null;
}

export type ChipTone = 'pos' | 'warn' | 'neg' | 'default';

export const CONNECTION_STATUS_META: Record<
  PaymentProviderConnectionStatus,
  { tone: ChipTone; label: string }
> = {
  unverified: { tone: 'warn', label: '待验证' },
  connected: { tone: 'pos', label: '已连接' },
  invalid: { tone: 'neg', label: '已失效' },
};

export function chipClass(tone: ChipTone): string {
  return tone === 'default' ? 'chip' : `chip chip-${tone}`;
}

/** 最近测试结果是否针对当前版本：never = 从未测试；stale = 针对旧版本，需重新测试。 */
export function lastTestFreshness(
  config: Pick<PaymentProviderConfigResponse, 'last_test_result' | 'config_version'>,
): 'never' | 'current' | 'stale' {
  if (!config.last_test_result) return 'never';
  return config.last_test_result.config_version === config.config_version ? 'current' : 'stale';
}

/** App 行副标题：`v{版本} · 验证 / 测试口径 · 回跳`。 */
export function configSummaryLine(
  config: PaymentProviderConfigResponse,
  platform: ProviderPlatform | null,
): string {
  const parts = [`v${config.config_version}`];
  const last = config.last_test_result;
  if (last && last.config_version !== config.config_version) {
    parts.push(
      `上次测试${last.status === 'connected' ? '通过' : '未通过'}的是 v${last.config_version}（${fmtDateTime(last.tested_at)}）`,
    );
  } else if (config.last_verified_at) {
    parts.push(`最近验证 ${fmtDateTime(config.last_verified_at)}`);
  } else if (last) {
    parts.push('从未成功验证');
  } else {
    parts.push('从未测试');
  }
  if (platform === 'WEB') {
    parts.push('原生回跳 不适用（Web App）');
  } else {
    parts.push(
      config.native_return_base_url ? `回跳 ${config.native_return_base_url}` : '回跳 未填写',
    );
  }
  return parts.join(' · ');
}

/** 未配置 App = 能力声明平台适用 − 已有配置（按 App 名排序）。 */
export function unconfiguredApps(
  apps: AppRow[],
  configs: Pick<PaymentProviderConfigResponse, 'app_id'>[],
  platforms: readonly ProviderPlatform[],
): AppRow[] {
  const configured = new Set(configs.map((c) => c.app_id));
  return apps
    .filter((app) => {
      const platform = providerPlatformOf(app);
      return platform !== null && platforms.includes(platform) && !configured.has(app.id);
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** endpoint_id 缩写：首 8 位 + 末 4 位，与设计稿一致。 */
export function shortEndpointId(endpointId: string): string {
  return endpointId.length > 14 ? `${endpointId.slice(0, 8)}…${endpointId.slice(-4)}` : endpointId;
}
